<template>
  <a-card class="quote-total-costs">
    <v-col>
      <div class="quote-section-title">Total Costs</div>

      <div class="quote-section-block quote-total-costs-block">
        <div class="quote-total-costs-row">
          <div>
            <table v-if="data.peopleRanges.length > 0" class="quote-section-table">
              <thead>
                <tr>
                  <th class="quote-section-table__reorder"></th>
                  <th class="quote-section-table__no">No</th>
                  <th>Min</th>
                  <th>Max</th>
                  <th>Cost per PAX</th>
                  <th class="quote-section-table__actions">Delete</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(peopleRange, index) in data.peopleRanges" :key="index" class="quote-section-table__row">
                  <td class="quote-section-table__reorder" align="center">
                    <v-btn icon x-small :disabled="readonly" @click="reorderPeopleRangeDown(index)">
                      <v-icon small>mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn icon x-small :disabled="readonly" @click="reorderPeopleRangeUp(index)">
                      <v-icon small>mdi-arrow-up</v-icon>
                    </v-btn>
                  </td>
                  <td class="quote-section-table__no">{{ index + 1 }}</td>
                  <td>
                    <f-number
                      :value="peopleRange.min"
                      :disabled="readonly"
                      @input="(v) => updatePeopleRangeByIndex(v, index)"
                      @keydown="(e) => onKeyDown(e)"
                    />
                  </td>
                  <td>
                    <f-number v-model="peopleRange.max" :disabled="readonly" @keydown="(e) => onKeyDown(e)" />
                  </td>
                  <td>
                    <a-hover-menu>
                      <template v-slot:activator="{ on }">
                        <QuotePax
                          :key="index"
                          :value="Number(getTotalCosts()[index]).toFixed(2)"
                          :disabled="true"
                          :unit="'per PAX'"
                        />
                      </template>
                      <div v-html="getTotalCostAndFOCInfo(peopleRange.min)"></div>
                    </a-hover-menu>
                  </td>
                  <td class="quote-section-table__actions" align="center">
                    <v-btn icon x-small :disabled="readonly" @click="removePeopleRange(index)">
                      <v-icon small>clear</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="data.peopleRanges.length < 10" class="quote-section-footer">
              <v-btn small text color="primary" class="quote-section-add-line" :disabled="readonly" @click="addPeopleRange">
                <v-icon left small>mdi-plus</v-icon>
                Add new line
              </v-btn>
            </div>
          </div>

          <div v-if="productTypeBreakdown.total > 0" class="quote-total-costs-breakdown">
            <table class="quote-total-costs-table">
              <thead>
                <tr>
                  <th colspan="4">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="quote-total-costs-help-label" v-bind="attrs" v-on="on">Cost by product type</span>
                      </template>
                      <span>
                        Total trip cost split by product type for the highest people range ({{ highestRangeLabel }}),
                        using the same cost calculation rules as the Total Costs table.
                      </span>
                    </v-tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr class="quote-total-costs-table__total-row">
                  <td class="quote-total-costs-table__label quote-total-costs-table__label--total">Total costs</td>
                  <td>
                    <div class="quote-total-costs-inline">
                      <f-number :value="rowPerPerson(productTypeBreakdown.total)" smlabel disabled />
                      <span class="quote-total-costs-unit">{{ getCurrency() }} / PAX</span>
                    </div>
                  </td>
                  <td>
                    <div class="quote-total-costs-inline">
                      <f-number :value="rowAmount(productTypeBreakdown.total)" smlabel disabled />
                      <span class="quote-total-costs-unit">{{ getCurrency() }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="quote-total-costs-inline">
                      <f-number :value="100" smlabel disabled />
                      <span class="quote-total-costs-unit">%</span>
                    </div>
                  </td>
                </tr>
                <tr
                  v-for="row in productTypeBreakdown.rows"
                  :key="row.productType"
                  class="quote-product-type-row"
                  :style="getProductTypeCssVars(row.productType)"
                >
                  <td class="quote-total-costs-table__label">
                    <span class="quote-total-costs-type-swatch" :style="{ backgroundColor: getProductTypeAccent(row.productType) }"></span>
                    {{ row.label }}
                  </td>
                  <td>
                    <div class="quote-total-costs-inline">
                      <f-number :value="rowPerPerson(row.amount)" smlabel disabled />
                      <span class="quote-total-costs-unit">{{ getCurrency() }} / PAX</span>
                    </div>
                  </td>
                  <td>
                    <div class="quote-total-costs-inline">
                      <f-number :value="rowAmount(row.amount)" smlabel disabled />
                      <span class="quote-total-costs-unit">{{ getCurrency() }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="quote-total-costs-inline">
                      <f-number :value="rowPercent(row.percent)" smlabel disabled />
                      <span class="quote-total-costs-unit">%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </v-col>
  </a-card>
</template>

<script>
import QuotePax from "./QuotePax";
import quoteUtils from "@/modules/quotes/components/utils";
import { getQuoteProductTypeColors, getQuoteProductTypeCssVars } from "@/modules/quotes/components/quoteProductTypeColors";
import moneyService from "@/services/money";
export default {
  components: {
    QuotePax,
  },
  props: {
    value: Object,
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    data: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
    highestRangeIndex() {
      return quoteUtils.getHighestPeopleRangeIndex(this.data.peopleRanges);
    },
    highestRangeLabel() {
      const range = this.data.peopleRanges[this.highestRangeIndex];
      if (!range) return "";
      return `${range.min}-${range.max} PAX`;
    },
    productTypeBreakdown() {
      const range = this.data.peopleRanges[this.highestRangeIndex];
      if (!range) return { rows: [], total: 0, pax: 0 };

      return quoteUtils.getCostBreakdownByProductType(this.data.costs, this.data.logisticsCosts, range.min);
    },
  },
  methods: {
    getProductTypeCssVars(productType) {
      return getQuoteProductTypeCssVars(productType);
    },
    getProductTypeAccent(productType) {
      return getQuoteProductTypeColors(productType).accent;
    },
    getTotalCosts() {
      return quoteUtils.getTotalCosts(this.data.costs, this.data.logisticsCosts, this.data.peopleRanges);
    },
    getTotalCostAndFOCInfo(min) {
      const currency = this.getCurrency();
      const focInfo = quoteUtils.getFOCInfo(this.data.costs, this.data.logisticsCosts, min);
      const lines = [
        `Total Per Pax: ${currency}${focInfo.total.toFixed(2)}`,
        `Total : ${currency}${(focInfo.total * min).toFixed(2)}`,
        ``,
        `Total FOC : ${currency}${focInfo.totalFoc.toFixed(2)}`,
        ``,
      ];
      Object.values(focInfo.focInfo).forEach((value) => {
        lines.push(value.name);
        Object.values(value.products).forEach((product) => {
          lines.push(`- ${product.name} x${product.quantity} : ${currency}${product.value.toFixed(2)}`);
        });
        lines.push("");
      });
      return lines.join("<br>");
    },
    rowAmount(amount) {
      return Math.round(amount);
    },
    rowPerPerson(amount) {
      const pax = this.productTypeBreakdown.pax;
      if (!pax) return 0;
      return Math.round(amount / pax);
    },
    rowPercent(percent) {
      return Math.round(percent);
    },
    addPeopleRange() {
      if (this.readonly) return;
      this.data.peopleRanges.push({ min: 0, max: 0 });
      this.data.margin.percentDynamic.push(0);
      this.data.margin.perPax.push(0);
      this.data.margin.perGroup.push(0);
      this.forEachComparisonMargin((margin) => {
        margin.percentDynamic.push(0);
        margin.perPax.push(0);
        margin.perGroup.push(0);
      });
    },
    removePeopleRange(index) {
      if (this.readonly) return;
      this.data.peopleRanges.splice(index, 1);
      this.data.margin.percentDynamic.splice(index, 1);
      this.data.margin.perPax.splice(index, 1);
      this.data.margin.perGroup.splice(index, 1);
      this.forEachComparisonMargin((margin) => {
        margin.percentDynamic.splice(index, 1);
        margin.perPax.splice(index, 1);
        margin.perGroup.splice(index, 1);
      });
    },
    reorderPeopleRangeDown(index) {
      if (this.readonly) return;
      const nextIndex = index + 1;
      if (nextIndex >= this.data.peopleRanges.length) return;
      this.swapPeopleRanges(index, nextIndex);
    },
    reorderPeopleRangeUp(index) {
      if (this.readonly) return;
      const previousIndex = index - 1;
      if (previousIndex < 0) return;
      this.swapPeopleRanges(index, previousIndex);
    },
    swapPeopleRanges(fromIndex, toIndex) {
      const ranges = this.data.peopleRanges;
      const tempRange = ranges[fromIndex];
      this.$set(ranges, fromIndex, ranges[toIndex]);
      this.$set(ranges, toIndex, tempRange);

      this.swapMarginIndex(this.data.margin, fromIndex, toIndex);
      this.forEachComparisonMargin((margin) => this.swapMarginIndex(margin, fromIndex, toIndex));
    },
    swapMarginIndex(margin, fromIndex, toIndex) {
      if (!margin) return;
      ["percentDynamic", "perPax", "perGroup"].forEach((key) => {
        const values = margin[key];
        if (!Array.isArray(values)) return;
        const temp = values[fromIndex];
        this.$set(values, fromIndex, values[toIndex]);
        this.$set(values, toIndex, temp);
      });
    },
    forEachComparisonMargin(callback) {
      (this.data.comparisonPeriods || []).forEach((period) => {
        if (period?.margin) callback(period.margin);
      });
    },
    updatePeopleRangeByIndex(v, index) {
      if (this.readonly) return;
      this.data.peopleRanges[index].min = v;
    },
    onKeyDown(e) {
      if (e.key === "." || e.key === "-" || e.key === "+") e.preventDefault();
    },
    getCurrency() {
      const workingEnv = this.$store.getters["AccountStore/environment"];
      const source = moneyService.getCurrencySource(workingEnv);
      return source.currency_symbol;
    },
  },
};
</script>

<style scoped>
.quote-section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.quote-section-block {
  display: inline-block;
  width: max-content;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  vertical-align: top;
}

.quote-total-costs-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 48px;
}

.quote-section-table {
  width: max-content;
  max-width: 100%;
  border-collapse: collapse;
}

.quote-section-table th {
  padding: 4px 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #666;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
}

.quote-section-table td {
  padding: 4px 6px;
  vertical-align: middle;
  border-bottom: 1px solid #efefef;
}

.quote-section-table__row:hover td {
  background: #fafafa;
}

.quote-section-table__no {
  width: 32px;
  text-align: center;
}

.quote-section-table__reorder {
  width: 56px;
  white-space: nowrap;
  text-align: center;
}

.quote-section-table__actions {
  width: 44px;
  text-align: center;
}

.quote-section-table >>> .v-input,
.quote-section-table >>> .v-text-field {
  width: 84px;
  min-width: 84px;
}

.quote-section-footer {
  padding-top: 2px;
}

.quote-section-add-line {
  text-transform: none !important;
  letter-spacing: 0 !important;
  padding-left: 0 !important;
}

.quote-total-costs-breakdown {
  flex: 0 0 auto;
  padding: 4px 8px 6px;
  background: #e8e8e8;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  box-sizing: border-box;
}

.quote-total-costs-table {
  border-collapse: collapse;
  width: max-content;
}

.quote-total-costs-table th {
  padding: 4px 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #666;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
}

.quote-total-costs-help-label {
  cursor: help;
  border-bottom: 1px dotted #bbb;
}

.quote-total-costs-table td {
  padding: 4px 6px;
  vertical-align: middle;
  border-bottom: 1px solid #efefef;
  white-space: nowrap;
}

.quote-total-costs-type-swatch {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.quote-total-costs-table__label {
  font-weight: 400;
  font-size: 13px;
  color: #555;
  padding-right: 8px !important;
}

.quote-total-costs-table__label--total {
  font-weight: 600;
}

.quote-total-costs-table__total-row td {
  border-bottom: 1px solid #d0d0d0;
}

.quote-total-costs-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.quote-total-costs-inline >>> .v-input,
.quote-total-costs-inline >>> .v-text-field {
  width: 100px;
  min-width: 100px;
  flex: 0 0 100px;
}

.quote-total-costs-unit {
  color: #666;
  font-size: 12px;
  white-space: nowrap;
}
</style>
